import React, { createContext, useContext, useEffect } from "react";
import { useUserPreferencesStore } from "../store/userPreferencesStore";
import { useLanguageStore } from "../store/languageStore";
import { useDarkModeStore } from "../store/darkModeStore";

interface PreferencesContextType {
  syncPreferences: () => void;
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error("usePreferences must be used within a PreferencesProvider");
  }
  return context;
};

interface PreferencesProviderProps {
  children: React.ReactNode;
}

export const PreferencesProvider: React.FC<PreferencesProviderProps> = ({
  children,
}) => {
  const { interface: interfacePrefs, updateInterface } = useUserPreferencesStore();
  const { selectedLanguage, setLanguage } = useLanguageStore();
  const { isDarkMode, setDarkMode } = useDarkModeStore();

  // Sync language between stores
  useEffect(() => {
    if (interfacePrefs.language !== selectedLanguage) {
      const updateLanguage = async () => {
        try {
          await setLanguage(interfacePrefs.language as "EN" | "ES");
        } catch (error) {
          console.error("Failed to update language:", error);
        }
      };
      updateLanguage();
    }
  }, [interfacePrefs.language]);

  // Sync theme between stores
  useEffect(() => {
    const handleThemeChange = () => {
      if (interfacePrefs.theme === "dark" && !isDarkMode) {
        setDarkMode(true);
      } else if (interfacePrefs.theme === "light" && isDarkMode) {
        setDarkMode(false);
      } else if (interfacePrefs.theme === "auto") {
        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        setDarkMode(mediaQuery.matches);
      }
    };

    handleThemeChange();

    // Listen for system theme changes if in auto mode
    if (interfacePrefs.theme === "auto") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = (e: MediaQueryListEvent) => {
        setDarkMode(e.matches);
      };

      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
  }, [interfacePrefs.theme, isDarkMode, setDarkMode]);

  // Update preferences when language store changes
  useEffect(() => {
    if (selectedLanguage !== interfacePrefs.language) {
      updateInterface({ language: selectedLanguage });
    }
  }, [selectedLanguage]);

  const syncPreferences = () => {
    // Force sync all preferences
    setLanguage(interfacePrefs.language as "EN" | "ES");

    if (interfacePrefs.theme === "dark") {
      setDarkMode(true);
    } else if (interfacePrefs.theme === "light") {
      setDarkMode(false);
    } else if (interfacePrefs.theme === "auto") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      setDarkMode(mediaQuery.matches);
    }
  };

  return (
    <PreferencesContext.Provider value={{ syncPreferences }}>
      {children}
    </PreferencesContext.Provider>
  );
};
